import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import DropDown from "../../elements/Dropdown";
import PieChartComponent from "../../elements/PieChart";
import ProductCard from "../../elements/ProductCard";
import StackedBarChart from "../../elements/StackedBarChart";
import styles from "./index.module.css";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import {
  categorySteps,
  dfProductData,
  locationSteps,
  priceRange,
  productData,
  rowCounts,
  sortingMapping,
  sortingSteps,
  storeSteps,
  summary_notes,
  terminalSteps,
} from "../../../utils/constant";
import DfProductCard from "../../elements/DfProductCard";
import Header from "../../elements/Header";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAllProductsAsyncThunk,
  setAllProduct,
  setMeta,
  setSearch,
} from "../../../store/slices/Products/product.slice";
import { fetchWatchLists } from "../../../store/slices/watchlist/data.slice";
import { useLocation } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { filterLocationsByOrg } from "../../../utils/helpers";
import MultiselectDropdownWithBreakdown from "../../elements/MultiselectDropdownWithBreakdown";
import AIBox from "../../elements/AIBox";
import {
  fetchAISummaryFromDataAsyncThunk,
  resetSummary,
} from "../../../store/slices/ai/ai.slice";

const priceRankSteps = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const DfProducts = (props) => {
  const queryString = useLocation().search;
  const queryParams = new URLSearchParams(queryString);

  const flattenOptions = (groupedOptions) => {
    return groupedOptions.flatMap((group) => group.options);
  };

  const formatStepsForDropdown = (options) => {
    // Check if source is one of the New Zealand sources
    const nzSources = ['aelia_auckland', 'aelia_queenstown', 'aelia_christchurch'];
    
    if (nzSources.includes(source)) {
      // Return current output for New Zealand sources
      return [
        {
          heading: "beauty",
          options: ["fragrance", "gift_sets", "makeup", "skin_care"],
        },
        {
          heading: "spirits",
          options: [
            "baijiu",
            "brandy",
            "cognac",
            "gin",
            "liqueurs",
            "mini_bottles",
            "premium_spirits",
            "rum",
            "tequila",
            "vodka",
            "whisky",
          ],
        },
        {
          heading: "wine",
          options: [
            "fortified",
            "premium_wine",
            "red",
            "rose",
            "sparkling_champagne",
            "white",
          ],
        },
        {
          heading: "confectionery",
          options: [
            "chocolates",
            "food_staples",
            "health_food",
            "lollies",
            "mints",
            "snacks_and_sweets",
            "souvenir_food",
          ],
        },
      ];
    } else if (source === 'heinemann_sydney') {
      // Return specific output for heinemann_sydney
      return [
        {
          heading: "whisky",
          options: [
            "american_whisky",
            "blended_whisky",
            "canadian_whisky",
            "irish_whisky",
            "japanese_whisky",
            "scotch_whisky",
            "single_malt_whisky"
          ]
        },
        {
          heading: "rum",
          options: ["rum"]
        },
        {
          heading: "wine",
          options: ["red_wine", "rose_wine", "sparkling_wine"]
        },
        {
          heading: "liqueur & aperitif",
          options: ["bitter_aperitif", "liqueur", "sherry_port"]
        },
        {
          heading: "cognac & brandy",
          options: ["cognac_brandy"]
        },
        {
          heading: "gin",
          options: ["gin"]
        },
        {
          heading: "vodka",
          options: ["vodka"]
        },
        {
          heading: "tequila",
          options: ["tequila"]
        },
        {
          heading: "champagne",
          options: ["champagne"]
        },
        {
          heading: "beauty",
          options: [
            "blusher",
            "concealer",
            "eye_shadow",
            "eyebrows",
            "eyeliner",
            "foundation",
            "lipstick_and_lipliner",
            "makeup_sets",
            "mascara",
            "powders"
          ]
        },
        {
          heading: "skin_care",
          options: [
            "bath_and_shower",
            "body_care",
            "bodycare_care",
            "cleansing",
            "eye_care",
            "hair_care",
            "hand_care",
            "masks",
            "men_skincare",
            "serum",
            "skin_care_sets",
            "sun_care",
            "care"
          ]
        },
        {
          heading: "fragrance",
          options: [
            "home_fragrance_candle",
            "men_fragrance",
            "men_fragrance_set",
            "women_fragrance",
            "women_fragrance_set"
          ]
        },
        {
          heading: "toiletries",
          options: ["toiletries"]
        },
        {
          heading: "accessories",
          options: ["accessories"]
        },
        {
          heading: "confectionery",
          options: [
            "chocolates",
            "confectionery",
            "food_staples",
            "health_food",
            "lollies",
            "mints",
            "snacks_and_sweets",
            "souvenir_food",
          ],
        },
      ];
    } else {
      // Default fallback for other sources
      return [
        {
          heading: "beauty",
          options: ["fragrance", "gift_sets", "makeup", "skin_care"],
        },
        {
          heading: "spirits",
          options: [
            "baijiu",
            "brandy",
            "cognac",
            "gin",
            "liqueurs",
            "mini_bottles",
            "premium_spirits",
            "rum",
            "tequila",
            "vodka",
            "whisky",
          ],
        },
        {
          heading: "wine",
          options: [
            "fortified",
            "premium_wine",
            "red",
            "rose",
            "sparkling_champagne",
            "white",
          ],
        },
        {
          heading: "confectionery",
          options: [
            "chocolates",
            "food_staples",
            "health_food",
            "lollies",
            "mints",
            "snacks_and_sweets",
            "souvenir_food",
          ],
        },
      ];
    }
  };

  const dispatch = useDispatch();
  const brandSteps = useSelector((store) => {
    console.log("Redux brandSteps selector called:", store.constant.brand);
    return store.constant.brand;
  });
  const locationSteps = useSelector((store) => store.constant.location);
  const organizationId = useSelector((store) => store.user.organizationId);
  const { source, currency } = useSelector((store) => {
    console.log("Redux source/currency selector called:", { source: store.constant.source, currency: store.constant.currency });
    return store.constant;
  });
  const subcategorySteps = useSelector((store) => store.constant.sub_category);
  
  // Filter locations based on organization ID (hide Adelaide and Cairns for org ID 3)
  const filteredLocationSteps = useMemo(() => {
    return filterLocationsByOrg(locationSteps, organizationId);
  }, [locationSteps, organizationId]);

  const [sorting, setSorting] = useState(0);
  let { product: products, loading: productsLoading } = useSelector(
    (store) => store.products
  );
  const searchInput = useSelector((store) => store.products.search);
  const [selectedCategories, setSelectedCategories] = useState(categorySteps);
  const [selectedSubCategories, setSelectedSubCategories] = useState(
    subcategorySteps.length > 0 ? formatStepsForDropdown(subcategorySteps) : []
  );
  const [selectedBrands, setSelectedBrands] = useState(brandSteps);
  const [selectedLocations, setSelectedLocations] = useState(filteredLocationSteps || []);
  const [selectedPriceRank, setSelectedPriceRank] = useState(priceRankSteps);
  const [selectedPriceRange, setSelectedPriceRange] = useState(
    queryParams.get("pricerange")
      ? priceRange.indexOf(queryParams.get("pricerange"))
      : 0
  );
  const [localSearch, setLocalSearch] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const {
    watchLists,
    loading: watchlistLoading,
    error,
  } = useSelector((store) => store.watchlist);
  const [selRowCount, setSelRowCount] = useState(0);
  const [selPage, setSelPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(null);
  const { loadingLocation, loadingSubCategory } = useSelector(
    (store) => store.constant
  );

  async function getAllProducts({
    offset = (selPage - 1) * rowCounts[selRowCount],
    limit = rowCounts[selRowCount],
    category = selectedCategories.join(",").toLowerCase(),
    location = selectedLocations.join(",").toLowerCase(),
    pricerank = selectedPriceRank.join(",").toLowerCase(),
    sort = sortingMapping[sortingSteps[sorting]],
    pricerange = priceRange[selectedPriceRange] == "none"
      ? null
      : priceRange[selectedPriceRange],
    search = searchInput,
    brand = selectedBrands.join(",").toLowerCase(),
    sub_category = Array.isArray(selectedSubCategories) && selectedSubCategories.length > 0 && typeof selectedSubCategories[0] === 'string'
      ? selectedSubCategories.join(",").toLowerCase()
      : flattenOptions(selectedSubCategories).join(",").toLowerCase(),
  } = {}) {
    location = location + "," + source;
    const res = await dispatch(
      fetchAllProductsAsyncThunk({
        source,
        offset,
        limit,
        brand,
        sub_category,
        category,
        location,
        pricerank,
        sort,
        pricerange,
        search,
      })
    );
    if (res?.payload?.status == "success") {
      setTotalProducts(res.payload.totals);
      dispatch(setAllProduct(res.payload.data));
      // dispatch(
      //   fetchAISummaryFromDataAsyncThunk({data:res.payload?.ai,notes:summary_notes?.replace("%source%",'aelia_auckland')})
      // );
    }
  }

  const timer = useRef(null);
  const debounce = (data) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!loadingLocation && !loadingSubCategory) {
        getAllProducts(data);
      }
    }, 600);
  };

  useEffect(() => {
    setSelPage(1);
    if (subcategorySteps.length !== 0) {
      debounce();
    }
  }, [searchInput, source]);



  const onSortingChange = (value, index) => {
    setSorting(index);
    switch (sortingSteps[index]) {
      case "Price - Low to High":
        debounce({ sort: "price_low_to_high" });
        break;
      case "Price - High to Low":
        debounce({ sort: "price_high_to_low" });
        break;
      case "Price Rank - Asc":
        debounce({ sort: "pricerank_low_to_high" });
        break;
      case "Price Rank - Dsc":
        debounce({ sort: "pricerank_high_to_low" });
        break;
      case "Difference - Low to High":
        debounce({ sort: "difference_low_to_high" });
        break;
      case "Difference - High to Low":
        debounce({ sort: "difference_high_to_low" });
        break;
      case "Difference % - Low to High":
        debounce({ sort: "difference_percentage_low_to_high" });
        break;
      case "Difference % - High to Low":
        debounce({ sort: "difference_percentage_high_to_low" });
        break;
    }
  };

  useEffect(() => {
    if (brandSteps.length > 0 && filteredLocationSteps.length > 7) {
      debounce();
    }
  }, [selectedBrands, selectedLocations, selectedSubCategories]);

  // Reset selectedSubCategories when source changes
  useEffect(() => {
    console.log("Subcategory reset effect:", { source, subcategorySteps: subcategorySteps?.length });
    if (subcategorySteps && subcategorySteps.length > 0) {
      const newSubCategories = formatStepsForDropdown(subcategorySteps);
      console.log("Setting subcategories:", newSubCategories);
      setSelectedSubCategories(newSubCategories);
    }
  }, [source, subcategorySteps]);

  // Reset selectedBrands when source changes
  useEffect(() => {
    console.log("Brand reset effect:", { source, brandSteps: brandSteps?.length });
    console.log("Brand reset effect - brandSteps:", brandSteps);
    console.log("Brand reset effect - current selectedBrands:", selectedBrands);
    if (brandSteps && brandSteps.length > 0) {
      console.log("Setting brands:", brandSteps);
      setSelectedBrands(brandSteps);
    }
  }, [source, brandSteps]);

  // Initialize selectedBrands when brandSteps changes (for initial load)
  useEffect(() => {
    if (brandSteps && brandSteps.length > 0 && selectedBrands.length === 0) {
      console.log("Initializing brands:", brandSteps);
      setSelectedBrands(brandSteps);
    }
  }, [brandSteps, selectedBrands.length]);

  // Reset selectedLocations when source changes
  useEffect(() => {
    console.log("Location reset effect:", { source, locationSteps: filteredLocationSteps?.length });
    if (filteredLocationSteps && filteredLocationSteps.length > 0) {
      console.log("Setting locations:", filteredLocationSteps);
      setSelectedLocations(filteredLocationSteps);
    }
  }, [source, filteredLocationSteps]);

  // Reset selectedCategories when source changes
  useEffect(() => {
    console.log("Category reset effect:", { source, categorySteps: categorySteps?.length });
    if (categorySteps && categorySteps.length > 0) {
      console.log("Setting categories:", categorySteps);
      setSelectedCategories(categorySteps);
    }
  }, [source, categorySteps]);

  // Monitor filter states for debugging
  useEffect(() => {
    console.log("Current filter states:", {
      selectedCategories: selectedCategories?.length,
      selectedSubCategories: selectedSubCategories?.length,
      selectedBrands: selectedBrands?.length,
      selectedLocations: selectedLocations?.length,
      selectedPriceRank: selectedPriceRank?.length
    });
    console.log("Current selectedBrands:", selectedBrands);
  }, [selectedCategories, selectedSubCategories, selectedBrands, selectedLocations, selectedPriceRank]);

  const onSelectedCategoryChange = (value) => {
    if (!isResetting) {
      setSelectedCategories(value);
      debounce({ category: value.join(",").toLowerCase() });
    }
  };

  const onSelectedBrandChange = (value) => {
    console.log("Brand change handler called with:", value?.length, "isResetting:", isResetting);
    console.log("Brand change handler value:", value);
    console.log("Current selectedBrands:", selectedBrands);
    console.log("Current brandSteps:", brandSteps);
    if (!isResetting) {
      console.log("Setting brands to:", value);
      setSelectedBrands(value);
    } else {
      console.log("Ignoring brand change during reset");
    }
  };

  const onSelectedSubCategoryChange = (value) => {
    if (!isResetting) {
      setSelectedSubCategories(value); // Keep flat array for internal dropdown working
      debounce({ sub_category: value.join(",").toLowerCase() });
    }
  };

  const onSelectedLocationChange = (value) => {
    if (!isResetting) {
      setSelectedLocations(value);
    }
  };

  const onSelectedPriceRankSteps = (value) => {
    setSelectedPriceRank(value);

    debounce({ pricerank: value.join(",").toLowerCase() });
  };

  const onSelectedPriceRange = (value, index) => {
    setSelectedPriceRange(index);
    debounce({ pricerange: value === "none" ? null : value });
  };
  const handleSearch = (e) => setLocalSearch(e?.target?.value);

  // Add a key to force re-render when source changes
  const [filterKey, setFilterKey] = useState(0);
  
  // Add a flag to prevent onChange calls during reset
  const [isResetting, setIsResetting] = useState(false);

  // Handle source change - reset all filters
  const handleSourceChange = (newSource) => {
    console.log("Source change triggered:", newSource);
    
    // Set resetting flag to prevent onChange calls
    setIsResetting(true);
    
    // Increment key to force re-render of all filter components
    setFilterKey(prev => prev + 1);
    
    // Reset Redux search state immediately
    dispatch(setSearch(""));
    
    // Reset other states that don't depend on source-specific data
    setLocalSearch("");
    setSelRowCount(0);
    setSelPage(1);
    setSorting(0);
    setSelectedPriceRange(0);
    
    // Reset price rank to all options
    setSelectedPriceRank(priceRankSteps);
    
    // Don't reset filters to empty arrays - let useEffects handle this
    console.log("Letting useEffects handle filter resets");
    
    // Set filters after a delay to ensure new data is loaded
    setTimeout(() => {
      console.log("Setting filters after delay");
      if (brandSteps && brandSteps.length > 0) {
        console.log("Setting brands to:", brandSteps.length);
        setSelectedBrands(brandSteps);
      }
      if (filteredLocationSteps && filteredLocationSteps.length > 0) {
        console.log("Setting locations to:", filteredLocationSteps.length);
        setSelectedLocations(filteredLocationSteps);
      }
      if (categorySteps && categorySteps.length > 0) {
        console.log("Setting categories to:", categorySteps.length);
        setSelectedCategories(categorySteps);
      }
      if (subcategorySteps && subcategorySteps.length > 0) {
        console.log("Setting subcategories to:", subcategorySteps.length);
        setSelectedSubCategories(formatStepsForDropdown(subcategorySteps));
      }
      
      // Clear resetting flag after setting filters
      setIsResetting(false);
      
      // Fetch fresh data with reset filters
      getAllProducts({
        limit: rowCounts[0],
        offset: 0,
      });
    }, 200);
  };

  const toggleFullscreen = (e) => setFullscreen((value) => !value);

  const getWatchLists = () => {
    dispatch(fetchWatchLists());
  };

  useEffect(() => {
    if (watchLists.length == 0) {
      getWatchLists();
    }
    return () => {
      dispatch(resetSummary());
    };
  }, []);

  // Handle search parameter from URL
  useEffect(() => {
    const searchParam = queryParams.get("search");
    if (searchParam) {
      dispatch(setSearch(decodeURIComponent(searchParam)));
    }
  }, [queryString, dispatch]);

  // Row count selection.
  const onSelectedRowCount = (value, index) => {
    setSelRowCount(index);
    getAllProducts({
      limit: value,
      offset: (selPage - 1) * value,
    });
  };

  const handlePage = (page, next) => {
    if (next === "next") {
      if (selPage < Math.ceil(totalProducts / rowCounts[selRowCount])) {
        getAllProducts({
          limit: rowCounts[selRowCount],
          offset: selPage * rowCounts[selRowCount],
        });
        setSelPage((prev) => prev + 1);
      }
    } else if (next == "prev") {
      getAllProducts({
        limit: rowCounts[selRowCount],
        offset: (selPage - 2) * rowCounts[selRowCount],
      });
      setSelPage((prev) => prev - 1);
    } else {
      getAllProducts({
        limit: rowCounts[selRowCount],
        offset: (page - 1) * rowCounts[selRowCount],
      });
      setSelPage(page);
    }
  };
  return (
    <div className={styles["container"]}>
      <Header isProducts={true} onSourceChange={handleSourceChange} />
      <hr />
      {/* <div className={styles["ai_box"]}>
        <AIBox isExpand={false} />
      </div> */}
      <div className={styles["filters"]}>
        <div>
          <div className={styles["filter"]}>
            <div>
              <div>Category</div>
              <div>
                <i class="bi bi-columns-gap"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              <MultiselectDropdown
                key={`category-${filterKey}`}
                steps={categorySteps}
                selSteps={
                  queryParams.get("category")
                    ? queryParams.get("category").split(",")
                    : selectedCategories
                }
                onChange={onSelectedCategoryChange}
                noLabel={true}
              />
            </div>
          </div>
          <div className={styles["filter"]}>
            <div>
              <div>Sub Category</div>
              <div>
                <i class="bi bi-columns-gap"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              {subcategorySteps.length > 0 && (
                <MultiselectDropdownWithBreakdown
                  key={`subcategory-${filterKey}`}
                  steps={formatStepsForDropdown(subcategorySteps)}
                  selSteps={
                    queryParams.get("sub_category")
                      ? queryParams
                          .get("sub_category")
                          .split(",")
                          .map((item) => decodeURIComponent(item))
                      : flattenOptions(formatStepsForDropdown(subcategorySteps))
                  }
                  onChange={onSelectedSubCategoryChange}
                  noLabel={true}
                />
              )}
            </div>
          </div>
          <div className={styles["filter"]}>
            <div>
              <div>Brand</div>
              <div>
                <i class="bi bi-tag"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              {brandSteps.length > 0 && (
                <MultiselectDropdown
                  key={`brand-${filterKey}`}
                  steps={brandSteps}
                  onChange={onSelectedBrandChange}
                  noLabel={true}
                  selSteps={
                    queryParams.get("brand")
                      ? queryParams.get("brand").split(",")
                      : selectedBrands
                  }
                />
              )}
              {console.log("Brand MultiselectDropdown props:", {
                key: `brand-${filterKey}`,
                stepsLength: brandSteps?.length,
                selStepsLength: selectedBrands?.length,
                selSteps: selectedBrands,
                isResetting
              })}
            </div>
          </div>
          <div className={styles["filter"]}>
            <div>
              <div>Location</div>
              <div>
                <i class="bi bi-pin"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              {filteredLocationSteps.length && (
                <MultiselectDropdown
                  key={`location-${filterKey}`}
                  steps={filteredLocationSteps}
                  onChange={onSelectedLocationChange}
                  noLabel={true}
                  selSteps={
                    queryParams.get("location")
                      ? queryParams.get("location").split(",")
                      : selectedLocations
                  }
                />
              )}
            </div>
          </div>
          <div className={styles["filter"]}>
            <div>
              <div>Price Rank</div>
              <div>
                <i class="bi bi-award"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              <MultiselectDropdown
                key={`pricerank-${filterKey}`}
                steps={priceRankSteps}
                onChange={onSelectedPriceRankSteps}
                noLabel={true}
                selSteps={
                  queryParams.get("pricerank")
                    ? queryParams.get("pricerank").split(",")
                    : selectedPriceRank
                }
              />
            </div>
          </div>
          <div className={styles["filter"]}>
            <div>
              <div>Price Range</div>
              <div>
                <i class="bi bi-coin"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              <DropDown
                steps={priceRange}
                onChange={onSelectedPriceRange}
                selectedValue={selectedPriceRange}
              />
            </div>
          </div>
        </div>
        <div>
          <div className={styles["filter"]}>
            <div>
              <div>Sort By</div>
              <div>
                <i class="bi bi-funnel"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              <DropDown
                steps={sortingSteps}
                selectedValue={sorting}
                onChange={onSortingChange}
              />
            </div>
          </div>
        </div>
      </div>
      <hr />

      <div
        className={
          styles["store"] + " " + (fullscreen ? styles["fullscreen"] : "")
        }
      >
        <div className={styles["store_head"]}>
          <div className={styles["store_title"]}>
            <div>Product List</div>
            <div>Showing {products.length} products</div>
          </div>
          <div className={styles["store_filters"]}>
            <div className={styles["expand"]} onClick={toggleFullscreen}>
              <div>
                <i class="bi bi-fullscreen"></i>
              </div>
            </div>
            {/* <div className={styles["export"]}>
              <div>Export</div>
              <div>
                <i class="bi bi-download"></i>
              </div>
            </div> */}
          </div>
        </div>

        <div className={styles["store_content"]}>
          <div>
            {products == -1 || productsLoading ? (
              <div className="loader-container">
                <BeatLoader />
              </div>
            ) : (
              products?.map((product) => (
                <div className={styles["store_product"]}>
                  <DfProductCard
                    {...product}
                    source={source}
                    currency={currency}
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Row count and pagination. */}
        <div className={styles["rowCount"]}>
          <div className={styles["count"]}>
            <span>Row Count</span>
            <div className={styles["countDropdown"]}>
              <DropDown
                steps={rowCounts}
                selectedValue={selRowCount}
                onChange={onSelectedRowCount}
              />
            </div>
          </div>

          <div className={styles["pages"]}>
            {selPage > 3 && (
              <span onClick={() => handlePage("", "prev")}>&lt;</span>
            )}
            <span
              onClick={() => handlePage(selPage > 3 ? selPage - 2 : 1)}
              className={
                selPage == (selPage > 3 ? selPage - 2 : 1)
                  ? styles["selected"]
                  : ""
              }
            >
              {selPage > 3 ? selPage - 2 : 1}
            </span>
            {Math.ceil(totalProducts / rowCounts[selRowCount]) > 1 && (
              <span
                onClick={() => handlePage(selPage > 3 ? selPage - 1 : 2)}
                className={
                  selPage == (selPage > 3 ? selPage - 1 : 2)
                    ? styles["selected"]
                    : ""
                }
              >
                {selPage > 3 ? selPage - 1 : 2}
              </span>
            )}

            {Math.ceil(totalProducts / rowCounts[selRowCount]) > 2 && (
              <span
                onClick={() => handlePage(selPage > 3 ? selPage : 3)}
                className={
                  selPage == (selPage > 3 ? selPage : 3)
                    ? styles["selected"]
                    : ""
                }
              >
                {selPage > 3 ? selPage : 3}
              </span>
            )}
            {Math.ceil(totalProducts / rowCounts[selRowCount]) > 3 && (
              <span onClick={() => handlePage("", "next")}>&gt;</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DfProducts;
